import { DivisionLabel } from "@cmp-src/data-display/labels/DivisionLabel";
import { OfficeLabel } from "@cmp-src/data-display/labels/OfficeLabel";
import { Division, Office } from "@gettactic/api";
import { useDivisionsMenuQuery } from "@gettactic/hooks";
import { divisionAndOfficesToCategorySelectItems } from "@v2/components/divisions/DivisionSelector/helpers";
import { CategorySelect } from "@v2/components/form/CategorySelect/CategorySelect";
import {
  Category,
  CategorySelectProps
} from "@v2/components/form/CategorySelect/types";
import { Button, ButtonProps } from "@v2/components/ui/button";
import { cn } from "@v2/lib/utils";
import React from "react";
import { DivisionSelectorWithApply } from "./DivisionSelectorWithApply";

export interface DivisionSelectorProps
  extends Omit<CategorySelectProps, "categories"> {
  selectedDivision?: Division | null;
  selectedOffice?: Office | null;
  onSelectDivision?: (division?: Division) => void;
  onSelectOffice?: (office?: Office) => void;
  hasAllItem?: boolean;
  hasNoOfficeOption?: boolean;
  noOfficeLabel?: string;
  triggerProps?: ButtonProps;
  withApply?: boolean;
  isMultiselect?: boolean;
  selectedOffices?: Office[];
  selectedDivisions?: Division[];
  onSelectOffices?: (offices: Office[]) => void;
  onSelectDivisions?: (divisions: Division[]) => void;
}

export const DivisionSelector: React.FC<DivisionSelectorProps> = (props) => {
  const {
    hasAllItem,
    hasNoOfficeOption,
    noOfficeLabel = "No Office",
    selectedDivision,
    selectedOffice,
    onSelectDivision,
    onSelectOffice,
    triggerProps,
    withApply,
    isMultiselect,
    selectedOffices,
    selectedDivisions,
    onSelectOffices,
    onSelectDivisions,
    ...rest
  } = props;

  // If withApply or isMultiselect is enabled, use the enhanced component
  if (withApply || isMultiselect) {
    return <DivisionSelectorWithApply {...props} />;
  }

  const divisions = useDivisionsMenuQuery();
  const root = divisions.data;

  const categories = React.useMemo(() => {
    const categories: Category = {
      name: "Divisions",
      id: "divisions",
      children: []
    };
    if (root?.tree) {
      categories.children =
        divisionAndOfficesToCategorySelectItems(root.tree)?.children || [];
    }

    // Add "No Office" option at the beginning if enabled
    if (hasNoOfficeOption) {
      categories.children = [
        {
          id: "no_office",
          name: noOfficeLabel,
          keywords: [noOfficeLabel, "none", "no office"],
          data: undefined
        },
        ...(categories.children || [])
      ];
    }

    return categories;
  }, [root?.tree, hasNoOfficeOption, noOfficeLabel]);

  const trigger = (
    <Button
      variant={"trigger"}
      {...triggerProps}
      className={cn("w-full min-w-0", triggerProps?.className)}
    >
      <span className={"min-w-0 flex-1 truncate text-left"}>
        {hasAllItem && !selectedOffice && !selectedDivision && (
          <span className={"truncate"}>All Offices and Divisions</span>
        )}
        {hasNoOfficeOption && !selectedOffice && !selectedDivision && (
          <span className={"truncate"}>{noOfficeLabel}</span>
        )}
        {selectedOffice && (
          <OfficeLabel
            office={selectedOffice}
            iconClassName={"text-neutral-1000"}
            className={"truncate min-w-0"}
          />
        )}
        {selectedDivision && (
          <span className={"truncate min-w-0 inline-flex"}>
            <DivisionLabel division={selectedDivision} />
          </span>
        )}
      </span>
    </Button>
  );

  return (
    <CategorySelect
      categories={categories}
      trigger={trigger}
      value={{
        name: selectedOffice?.name || selectedDivision?.name || (hasNoOfficeOption && !selectedOffice && !selectedDivision ? noOfficeLabel : ""),
        id: selectedOffice?.id || selectedDivision?.id || (hasNoOfficeOption && !selectedOffice && !selectedDivision ? "no_office" : "")
      }}
      onSelect={(category) => {
        if (category.id === "no_office") {
          onSelectOffice?.(undefined);
          onSelectDivision?.(undefined);
          return;
        }
        const isOffice = category.id.startsWith("offi_");
        if (isOffice) {
          onSelectOffice?.(category.data as Office);
        } else {
          onSelectDivision?.(category.data as Division);
        }
      }}
      {...rest}
    />
  );
};
